import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  type ColumnDef,
  type Row,
  type SortDirection,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { clsx } from 'clsx';
import { useState } from 'react';
import type { LinkProps } from 'turnip/Link/Link';
import { Offset } from 'turnip/Offset/Offset';
import {
  type BaseCellProps,
  type BodyCellProps,
  type CellType,
  type HeadCellProps,
  Table,
  type TableProps,
} from 'turnip/Table/Table';
import { baseClasses, validClasses } from 'turnip/TextInput/classes';
import { Pagination } from './Pagination';

type PowerTableColumnMeta = {
  align?: BaseCellProps['align'];
  width?: HeadCellProps['width'];
  noWrap?: boolean | CellType;
  pin?: BaseCellProps['pin'];
  lastPinned?: BaseCellProps['lastPinned'];
  tabularNumbers?: BodyCellProps['tabularNumbers'];
  bold?: BodyCellProps['bold'];
};

type PowerTableProps<TData> = Omit<TableProps, 'children'> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  data: TData[];
  showSearch?: boolean;
  showCaption?: boolean;
  initialSorting?: SortingState;
  enableSorting?: boolean;
  pagination?: boolean;
  initialPageSize?: number;
  showFirstAndLastPageButtons?: boolean;
  rowLink?: Omit<LinkProps, 'to'> & {
    /** A function that returns the URL for the row link based on the row data */
    toFn: (row: Row<TData>) => string;
    /** Index of the column containing the primary link */
    primary: number;
    /** Indexes of the columns containing secondary links */
    secondary: number[];
  };
  children?: React.ReactNode;
};

/**
 * This wires TanStack Table up to our Table UI components.
 *
 * See the stories for examples of usage. If you're having trouble, you may
 * want to familiarize yourself with the Table component and TanStack Table.
 */
function PowerTable<TData extends object>({
  columns,
  data,
  showSearch = false,
  showCaption,
  enableSorting,
  initialSorting,
  pagination,
  initialPageSize = 10,
  showFirstAndLastPageButtons,
  minWidth = 'min-w-[768px]',
  rowLink,
  children,
  ...tableProps
}: PowerTableProps<TData>) {
  const [sorting, setSorting] = useState(initialSorting ?? []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    initialState: pagination
      ? {
          pagination: {
            pageIndex: 0,
            pageSize: initialPageSize,
          },
        }
      : undefined,
    state: { sorting },
    onSortingChange: setSorting,
    enableSorting: enableSorting ?? !!initialSorting,
    enableSortingRemoval: false,
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const totalRows = table.getFilteredRowModel().rows.length;

  const pageFirstRow = totalRows ? pageIndex * pageSize + 1 : 0;
  const pageMaxRow = (pageIndex + 1) * pageSize;
  const pageLastRow = Math.min(pageMaxRow, totalRows);
  const globalFilter = table.getState().globalFilter;
  const hasContentAboveTable = pagination || showCaption || showSearch;

  return (
    <div>
      {hasContentAboveTable && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {showCaption && (
            <span className="font-bold">{tableProps.caption}</span>
          )}

          {showSearch && (
            <div className="order-last w-full md:order-none md:w-auto">
              <PowerTableSearch
                value={globalFilter}
                onChange={(e) => table.setGlobalFilter(e.target.value)}
              />
            </div>
          )}

          {pagination && (
            <div className="ml-auto">
              <Pagination
                pageFirstRow={pageFirstRow}
                pageLastRow={pageLastRow}
                totalRows={totalRows}
                previousEnabled={table.getCanPreviousPage()}
                previousOnClick={table.previousPage}
                nextEnabled={table.getCanNextPage()}
                nextOnClick={table.nextPage}
                showFirstAndLastPageButtons={showFirstAndLastPageButtons}
                firstOnClick={table.firstPage}
                lastOnClick={table.lastPage}
              />
            </div>
          )}
        </div>
      )}

      <Offset top={hasContentAboveTable ? 3 : 0}>
        <Table {...tableProps} minWidth={minWidth}>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.HeadRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const { noWrap, ...meta } =
                    header.column.columnDef.meta ?? {};
                  return (
                    <Table.HeadCell
                      key={header.id}
                      id={header.id}
                      {...meta}
                      noWrap={noWrap === 'head' || noWrap === true}
                      sortDirection={header.column.getIsSorted()}
                    >
                      <SortButton
                        userSortingEnabled={!!enableSorting}
                        columnIsSortable={header.column.getCanSort()}
                        direction={header.column.getIsSorted()}
                        nextDirection={header.column.getNextSortingOrder()}
                        onClick={header.column.getToggleSortingHandler()}
                        align={meta?.align}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </SortButton>
                    </Table.HeadCell>
                  );
                })}
              </Table.HeadRow>
            ))}
          </Table.Head>

          <Table.Body>
            {table.getRowModel().rows.map((row, pageRowIndex) => {
              const rowDisplayNumber = pageIndex * pageSize + pageRowIndex + 1;

              return (
                <Table.BodyRow key={row.id}>
                  {(active) =>
                    row.getVisibleCells().map((cell) => {
                      const { noWrap, ...meta } =
                        cell.column.columnDef.meta ?? {};
                      const context = cell.getContext();

                      const cellIndex = cell.column.getIndex();
                      const renderPrimaryLink = cellIndex === rowLink?.primary;
                      const renderSecondaryLink =
                        !!rowLink?.secondary.includes(cellIndex);
                      const hasLink =
                        rowLink && (renderPrimaryLink || renderSecondaryLink);

                      return (
                        <Table.BodyCell
                          key={cell.id}
                          {...meta}
                          noWrap={noWrap === 'body' || noWrap === true}
                          link={
                            hasLink
                              ? {
                                  ...getLinkProps(rowLink),
                                  to: rowLink.toFn(row),
                                  isPrimary: renderPrimaryLink,
                                }
                              : undefined
                          }
                        >
                          <div
                            className={clsx(
                              /*
                               * NOTE: this padding allows the text in the header and
                               * body cells of left-aligned sortable columns to remain
                               * vertically aligned once space is reserved in the header
                               * cell for the sorting icon.
                               */
                              enableSorting &&
                                cell.column.getCanSort() &&
                                (!meta.align || meta.align === 'left') &&
                                'pl-3'
                            )}
                          >
                            {cell.column.id === 'rowNumber'
                              ? rowDisplayNumber
                              : flexRender(cell.column.columnDef.cell, {
                                  ...context,
                                  row: {
                                    ...context.row,
                                    active,
                                    displayNumber: rowDisplayNumber,
                                  },
                                })}
                          </div>
                        </Table.BodyCell>
                      );
                    })
                  }
                </Table.BodyRow>
              );
            })}

            {globalFilter && table.getRowModel().rows.length === 0 && (
              <Table.BodyRow>
                <Table.BodyCell colSpan={columns.length}>
                  No results found for &quot;{globalFilter}&quot;
                </Table.BodyCell>
              </Table.BodyRow>
            )}

            {children}
          </Table.Body>
        </Table>
      </Offset>
    </div>
  );
}

const getLinkProps = <TData,>(
  rowLink: PowerTableProps<TData>['rowLink']
): Omit<LinkProps, 'to'> | undefined => {
  if (!rowLink) {
    return undefined;
  }

  const { toFn, primary, secondary, ...linkProps } = rowLink;

  return linkProps;
};

function SortButton({
  userSortingEnabled,
  columnIsSortable,
  direction,
  nextDirection,
  onClick,
  align,
  children,
}: {
  userSortingEnabled: boolean;
  columnIsSortable: boolean;
  direction: SortDirection | false;
  nextDirection: SortDirection | false;
  onClick?: (arg?: unknown) => void;
  align: BaseCellProps['align'];
  children: React.ReactNode;
}) {
  if (!userSortingEnabled || !columnIsSortable) {
    return children;
  }

  const currentDirectionIcon = direction === 'asc' ? faArrowDown : faArrowUp;
  const nextDirectionIcon = nextDirection === 'asc' ? faArrowDown : faArrowUp;

  return (
    <button
      className={clsx(
        'fa-width-auto group inline-flex items-center gap-1',
        align === 'left' ? 'text-left' : 'text-right'
      )}
      aria-label={`Sort ${nextDirection === 'asc' ? 'ascending' : 'descending'}`}
      onClick={onClick}
    >
      <FontAwesomeIcon
        icon={nextDirectionIcon}
        className={clsx(
          'text-xs text-gray-400 opacity-0 transition duration-300 group-hover:opacity-100',
          nextDirection === 'asc' &&
            'tranform -translate-y-1/2 group-hover:translate-y-0',
          nextDirection === 'desc' &&
            'tranform translate-y-1/2 group-hover:translate-y-0',
          direction && 'hidden'
        )}
      />
      <FontAwesomeIcon
        icon={currentDirectionIcon}
        className={clsx('text-xs', !direction && 'hidden')}
      />

      {children}
    </button>
  );
}

function PowerTableSearch({
  value = '',
  onChange,
}: {
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}) {
  return (
    <input
      type="search"
      name="search-table"
      aria-label="Search table"
      value={value}
      onChange={onChange}
      placeholder="Search..."
      className={clsx(baseClasses, validClasses, 'pr-3 text-sm')}
    />
  );
}

type CustomRowContext = {
  active: boolean;
  /** The row number, starting at 1. Can be useful for customizing row cells */
  displayNumber: number;
};

declare module '@tanstack/table-core' {
  // This allows us to add our own properties to the type for ColumnMeta,
  // providing type definitions where the `meta` value is used.
  // Ref. https://tanstack.com/table/v8/docs/api/core/column-def#meta
  // @ts-expect-error @typescript-eslint/no-empty-object-type
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface ColumnMeta extends PowerTableColumnMeta {}

  // This allows us to add `active` to the row context passed to the cell
  // renderers, so that we can make cell contents respond to when a row is
  // hovered or focused.
  // @ts-expect-error @typescript-eslint/no-empty-object-type
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Row extends CustomRowContext {}
}

export { PowerTable };
export type { PowerTableColumnMeta };
